import { Helmet } from 'react-helmet-async';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, MapPin } from 'lucide-react';
import { useState, useEffect } from 'react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import apiService from '@/services/api';

interface CategoryData {
  id: string | number;
  categoryId?: number;
  name: string;
  description: string;
  subtitle?: string;
  image?: string;
  icon?: string;
  tags?: string[];
  service_count?: number;
}

interface Service {
  id: string;
  name: string;
  description?: string;
  category_id: number | string;
  base_price_pence?: number;
  is_active?: number;
}

// Service images mapping - professional images for each service
const SERVICE_IMAGES: Record<string, string> = {
  // Cleaning & Care
  'Commercial Office Carpet Cleaning': 'https://images.unsplash.com/photo-1595428774223-ef52624120d2?w=400&h=300&fit=crop',
  'Core Contract Cleaning': 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=400&h=300&fit=crop',
  'Frame Repair': 'https://images.unsplash.com/photo-1565636192335-14a305d2b126?w=400&h=300&fit=crop',
  'Periodic & Planned Deep Cleans': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',

  // Carpentry & Joinery
  'Bespoke Joinery': 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=400&h=300&fit=crop',
  'Cabinetry & Storage': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop',
  'Carpentry (1st Fix)': 'https://images.unsplash.com/photo-1577720643272-265f434b3ba3?w=400&h=300&fit=crop',
  'Carpentry (2nd Fix)': 'https://images.unsplash.com/photo-1577720643272-265f434b3ba3?w=400&h=300&fit=crop',
  'Door Installation': 'https://images.unsplash.com/photo-1565636192335-14a305d2b126?w=400&h=300&fit=crop',
  'Flooring': 'https://images.unsplash.com/photo-1533090161126-20d456d01def?w=400&h=300&fit=crop',
  'Hoarding Systems': 'https://images.unsplash.com/photo-1581092162562-40038f23a039?w=400&h=300&fit=crop',
  'Roofing Carpentry': 'https://images.unsplash.com/photo-1541193227744-86dbced319cc?w=400&h=300&fit=crop',
  'Skirting & Architraves': 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=400&h=300&fit=crop',
  'Staircases': 'https://images.unsplash.com/photo-1577720643272-265f434b3ba3?w=400&h=300&fit=crop',

  // Core Construction
  'Blockwork': 'https://images.unsplash.com/photo-1581576228313-1a0be7fa3398?w=400&h=300&fit=crop',
  'Bricklaying': 'https://images.unsplash.com/photo-1581092916550-e323b3c69ddc?w=400&h=300&fit=crop',
  'Concrete Works': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',
  'Foundations & Footings': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',
  'Groundworks': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',
  'Steel Installation': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',
  'Structural Repairs': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',
  'Underpinning': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',

  // Landscaping
  'Decking': 'https://images.unsplash.com/photo-1585772724684-38269d6639fd?w=400&h=300&fit=crop',
  'Fencing': 'https://images.unsplash.com/photo-1585772724684-38269d6639fd?w=400&h=300&fit=crop',
  'Garden Clearance': 'https://images.unsplash.com/photo-1585772724684-38269d6639fd?w=400&h=300&fit=crop',
  'Garden Maintenance': 'https://images.unsplash.com/photo-1585772724684-38269d6639fd?w=400&h=300&fit=crop',
  'Hedge Cutting': 'https://images.unsplash.com/photo-1585772724684-38269d6639fd?w=400&h=300&fit=crop',
  'Landscaping': 'https://images.unsplash.com/photo-1585772724684-38269d6639fd?w=400&h=300&fit=crop',
  'Pressure Washing': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',
  'Tree Surgery': 'https://images.unsplash.com/photo-1585772724684-38269d6639fd?w=400&h=300&fit=crop',
  'Turfing': 'https://images.unsplash.com/photo-1585772724684-38269d6639fd?w=400&h=300&fit=crop',

  // Electrical
  'CCTV Installation': 'https://images.unsplash.com/photo-1580894160552-21d80ecfa92f?w=400&h=300&fit=crop',
  'Consumer Unit (Fuse Box) Replacement': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',
  'EICR Inspection': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',
  'Electrical Installation': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',

  // Finishing & Interiors
  'Ceiling Installation': 'https://images.unsplash.com/photo-1578500494198-246f612d782b?w=400&h=300&fit=crop',
  'Drylining': 'https://images.unsplash.com/photo-1578500494198-246f612d782b?w=400&h=300&fit=crop',
  'Feature Walls': 'https://images.unsplash.com/photo-1578500494198-246f612d782b?w=400&h=300&fit=crop',
  'Painting & Decorating': 'https://images.unsplash.com/photo-1578500494198-246f612d782b?w=400&h=300&fit=crop',
  'Plastering': 'https://images.unsplash.com/photo-1578500494198-246f612d782b?w=400&h=300&fit=crop',
  'Rendering': 'https://images.unsplash.com/photo-1578500494198-246f612d782b?w=400&h=300&fit=crop',
  'Tiling': 'https://images.unsplash.com/photo-1578500494198-246f612d782b?w=400&h=300&fit=crop',
  'Wallpapering': 'https://images.unsplash.com/photo-1578500494198-246f612d782b?w=400&h=300&fit=crop',

  // Kitchens & Bathrooms
  'Bathroom Installation': 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=300&fit=crop',
  'Bathroom Refurbishment': 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=300&fit=crop',
  'Full Kitchen Installation': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop',
  'Kitchen Refurbishment': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop',
  'Vanity & Sanitaryware': 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=300&fit=crop',
  'Washrooms': 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=300&fit=crop',
  'Wet Rooms': 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=300&fit=crop',

  // Locks & Security
  'Lock Changes': 'https://images.unsplash.com/photo-1565183197515-bcd58e90f951?w=400&h=300&fit=crop',
  'Locksmith Services': 'https://images.unsplash.com/photo-1565183197515-bcd58e90f951?w=400&h=300&fit=crop',

  // Plumbing
  'Bathroom Plumbing': 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=300&fit=crop',
  'Boiler Installation': 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=400&h=300&fit=crop',
  'Central Heating Installation': 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=400&h=300&fit=crop',
  'Emergency Plumbing': 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=400&h=300&fit=crop',
  'Gas Engineering': 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=400&h=300&fit=crop',
  'General Plumbing': 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=400&h=300&fit=crop',
  'Hot Water Cylinders': 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=400&h=300&fit=crop',
  'Kitchen Plumbing': 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=400&h=300&fit=crop',
  'Leak Detection & Repair': 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=400&h=300&fit=crop',
  'Pipework Installation': 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=400&h=300&fit=crop',
  'Radiator Installation': 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=400&h=300&fit=crop',
  'Underfloor Heating': 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=400&h=300&fit=crop',
  'Unvented Systems': 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=400&h=300&fit=crop',

  // Property Maintenance
  'Furniture Assembly': 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400&h=300&fit=crop',
  'General Property Maintenance': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',
  'Planned Preventative Maintenance': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',
  'Reactive Maintenance': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',
  'Wall Mounting': 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop',

  // Roofing
  'Chimney Repairs': 'https://images.unsplash.com/photo-1541193227744-86dbced319cc?w=400&h=300&fit=crop',
  'Fascias, Soffits & Guttering': 'https://images.unsplash.com/photo-1541193227744-86dbced319cc?w=400&h=300&fit=crop',
  'Flat Roofing': 'https://images.unsplash.com/photo-1541193227744-86dbced319cc?w=400&h=300&fit=crop',
  'Leadwork': 'https://images.unsplash.com/photo-1541193227744-86dbced319cc?w=400&h=300&fit=crop',
  'Pitched Roofing': 'https://images.unsplash.com/photo-1541193227744-86dbced319cc?w=400&h=300&fit=crop',
  'Roof Inspections': 'https://images.unsplash.com/photo-1541193227744-86dbced319cc?w=400&h=300&fit=crop',
};

// Metadata lookup for visual design
const CATEGORY_META: Record<string, { image: string; subtitle: string; tags: string[] }> = {
  '6': {
    image: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=500&h=500&fit=crop',
    subtitle: 'Residential & Commercial',
    tags: ['END-OF-TENANCY', 'DEEP-CLEAN', 'AIRBNB'],
  },
  '14': {
    image: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=500&h=500&fit=crop',
    subtitle: 'Custom Woodwork',
    tags: ['JOINERY', 'CUSTOM', 'INSTALLATIONS'],
  },
  '15': {
    image: 'https://images.unsplash.com/photo-1581576228313-1a0be7fa3398?w=500&h=500&fit=crop',
    subtitle: 'Professional Engineering',
    tags: ['STRUCTURAL', 'ENGINEERING', 'MAJOR-WORKS'],
  },
  '16': {
    image: 'https://images.unsplash.com/photo-1585772724684-38269d6639fd?w=500&h=500&fit=crop',
    subtitle: 'Garden Design',
    tags: ['LANDSCAPING', 'GARDENS', 'OUTDOOR'],
  },
  '17': {
    image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=500&h=500&fit=crop',
    subtitle: 'Smart & Safe',
    tags: ['EICR', 'INSTALLATION', 'SMART-HOME'],
  },
  '18': {
    image: 'https://images.unsplash.com/photo-1578500494198-246f612d782b?w=500&h=500&fit=crop',
    subtitle: 'Interior Design',
    tags: ['PAINTING', 'DECORATING', 'FINISHING'],
  },
  '19': {
    image: 'https://images.unsplash.com/photo-1607410632539-29a1a9b7cdf8?w=500&h=500&fit=crop',
    subtitle: 'Renovation',
    tags: ['KITCHENS', 'BATHROOMS', 'REFIT'],
  },
  '20': {
    image: 'https://images.unsplash.com/photo-1565183197515-bcd58e90f951?w=500&h=500&fit=crop',
    subtitle: 'Security Systems',
    tags: ['LOCKS', 'CCTV', 'ACCESS-CONTROL'],
  },
  '21': {
    image: 'https://images.unsplash.com/photo-1584622181563-430f63602d4b?w=500&h=500&fit=crop',
    subtitle: 'Heating & Gas',
    tags: ['BOILER', 'PLUMBING', 'GAS-SAFE'],
  },
  '22': {
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=500&h=500&fit=crop',
    subtitle: 'General Maintenance',
    tags: ['HANDYMAN', 'REPAIRS', 'MAINTENANCE'],
  },
  '23': {
    image: 'https://images.unsplash.com/photo-1541193227744-86dbced319cc?w=500&h=500&fit=crop',
    subtitle: 'Roof Experts',
    tags: ['ROOFING', 'INSPECTION', 'REPAIR'],
  },
  '24': {
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=500&h=500&fit=crop',
    subtitle: 'Compliance Services',
    tags: ['CERTIFICATION', 'COMPLIANCE', 'LEGAL'],
  },
  '25': {
    image: 'https://images.unsplash.com/photo-1578789358786-05dcc1a9ab0d?w=500&h=500&fit=crop',
    subtitle: 'Waste Management',
    tags: ['CLEARANCE', 'RECYCLING', 'DISPOSAL'],
  },
  '26': {
    image: 'https://images.unsplash.com/photo-1565636192335-14a305d2b126?w=500&h=500&fit=crop',
    subtitle: 'Windows & Doors',
    tags: ['GLAZING', 'INSTALLATION', 'FRAMES'],
  }
};

const ICON_EMOJI = {
  '6': '🧹',
  '14': '🪵',
  '15': '🏗️',
  '16': '🌿',
  '17': '⚡',
  '18': '🎨',
  '19': '🚿',
  '20': '🔒',
  '21': '🔧',
  '22': '🛠️',
  '23': '🏠',
  '24': '📋',
  '25': '♻️',
  '26': '🪟'
};

const ServiceCategoryDetailsPage = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const [category, setCategory] = useState<CategoryData | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const handleRequestService = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      navigate('/login');
      return;
    }

    // Get user role from localStorage userData
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        const userRoles = userData.roles || [userData.role] || [];

        // Only allow customers to request services
        if (userRoles.includes('admin') || userRoles.includes('provider')) {
          // Admin/Provider cannot request services
          alert('Please log in as a customer to request a service.');
          return;
        } else {
          // Customer role - go to customer dashboard request service page
          navigate('/dashboard/request-service');
        }
      } else {
        // No userData found, assume customer
        navigate('/dashboard/request-service');
      }
    } catch (error) {
      // If parsing fails, assume customer
      navigate('/dashboard/request-service');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        // Fetch all categories
        const categoriesRes = await apiService.get('/public/service-categories');
        if (categoriesRes.success && Array.isArray(categoriesRes.data)) {
          const foundCategory = categoriesRes.data.find(
            (cat: any) => String(cat.id) === String(categoryId) || String(cat.slug) === String(categoryId)
          );

          if (foundCategory) {
            const meta = CATEGORY_META[String(foundCategory.id)] || {};
            const enrichedCategory: CategoryData = {
              id: foundCategory.slug || String(foundCategory.id),
              categoryId: foundCategory.id,
              name: foundCategory.name,
              description: meta.description || foundCategory.description || '',
              subtitle: meta.subtitle || foundCategory.name,
              image: meta.image || 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=500&h=500&fit=crop',
              tags: meta.tags || [foundCategory.name.toUpperCase()],
              icon: ICON_EMOJI[String(foundCategory.id)] || '✨',
              service_count: foundCategory.service_count || 0
            };
            setCategory(enrichedCategory);

            // Fetch services for this category
            const servicesRes = await apiService.get(`/public/services/by-category/${foundCategory.id}`);
            if (servicesRes.success && Array.isArray(servicesRes.data)) {
              setServices(servicesRes.data);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching category details:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [categoryId]);

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-gray-600">Loading category details...</p>
        </div>
      </Layout>
    );
  }

  if (!category) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 text-lg mb-4">Category not found</p>
            <Link to="/services" className="text-teal-600 font-medium hover:text-teal-700">
              Back to Services
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{category.name} | Virewire Services</title>
        <meta name="description" content={category.description} />
      </Helmet>

      {/* Breadcrumb */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-600 hover:text-gray-900">HOME</Link>
            <span className="text-gray-400">/</span>
            <Link to="/services" className="text-gray-600 hover:text-gray-900">SERVICES</Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">{category.name.toUpperCase()}</span>
          </div>
        </div>
      </section>

      {/* Category Hero */}
      <section className="relative">
        <div className="relative h-64 sm:h-80 bg-gray-200 overflow-hidden">
          <img
            src={category.image}
            alt={category.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="text-5xl">{category.icon}</div>
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold mb-2">{category.name}</h1>
                <p className="text-lg text-gray-100">{category.subtitle}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {category.tags?.map(tag => (
                <span key={tag} className="text-xs bg-teal-600 px-3 py-1 rounded-full font-medium">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="bg-white border-b border-gray-200 py-12">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <p className="text-lg text-gray-700 leading-relaxed">{category.description}</p>
        </div>
      </section>

      {/* Services Cards */}
      <section className="bg-gray-50 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Our Services</h2>
            <p className="text-gray-600 text-lg">We offer {services.length} comprehensive services in this category</p>
          </div>

          {services.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, idx) => {
                const serviceImage = SERVICE_IMAGES[service.name] || 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop';
                return (
                  <div
                    key={service.id || idx}
                    className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 flex flex-col"
                  >
                    {/* Service Image */}
                    <div className="relative h-40 bg-gray-200 overflow-hidden">
                      <img
                        src={serviceImage}
                        alt={service.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                    </div>

                    {/* Service Content */}
                    <div className="p-5 flex-1 flex flex-col">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{service.name}</h3>

                      {service.description && (
                        <p className="text-sm text-gray-600 leading-relaxed flex-1">
                          {service.description}
                        </p>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-2 pt-4 border-t border-gray-200 mt-4">
                        <Button
                          variant="outline"
                          className="flex-1 h-9 text-xs font-medium border-teal-600 text-teal-600 hover:bg-teal-50 rounded-lg"
                        >
                          <Link to="#" className="w-full">Details</Link>
                        </Button>
                        <Button
                          onClick={handleRequestService}
                          className="flex-1 h-9 text-xs font-medium bg-teal-600 hover:bg-teal-700 text-white rounded-lg"
                        >
                          Request
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-lg p-12 text-center">
              <p className="text-gray-600 text-lg">Services coming soon in this category</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-teal-50 mb-8">
            Request a service or get a detailed quote for {category.name.toLowerCase()} services
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleRequestService}
              className="bg-white text-teal-600 hover:bg-gray-100 font-semibold text-lg px-8 py-3 rounded-lg flex items-center justify-center gap-2"
            >
              <span>Request a Service</span>
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button
              asChild
              variant="outline"
              className="border-white text-white hover:bg-teal-500 font-semibold text-lg px-8 py-3 rounded-lg"
            >
              <Link to="/contact" className="flex items-center justify-center gap-2">
                <span>Contact Us</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Quality Assurance Section */}
      <section className="bg-teal-50 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center gap-8">
            <MapPin className="h-8 w-8 text-teal-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-lg text-gray-900 font-medium mb-3">
                Every service is managed by the Virewire platform.
              </p>
              <div className="flex flex-wrap gap-6 text-sm text-gray-700">
                <span className="font-medium">✓ Quality Guaranteed</span>
                <span className="font-medium">✓ Vetted Professionals</span>
                <span className="font-medium">✓ 24/7 Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back Button */}
      <section className="bg-white py-8">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <Link to="/services" className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium">
            <ArrowLeft className="w-4 h-4" />
            Back to All Services
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default ServiceCategoryDetailsPage;
